/**
 * Hvilke panelseksjoner som står åpne, og hvorfor de ikke gjør det på mobil.
 *
 * ── Problemet ──────────────────────────────────────────────────────────────
 *
 * Planen vokste panel for panel, og hvert av dem var riktig å ha. Til sammen
 * ble de en side man måtte dra seg gjennom for å komme til det man kom for:
 * dagene. Rekkefølgen var værpanel, beste dag, optimalisering, budsjett,
 * klesråd, deling, lyd, kart, høydeprofil, trafikk, ferjer — og *så* ruta,
 * som nummer tolv.
 *
 * ── Hva som gjøres ─────────────────────────────────────────────────────────
 *
 * Ruta og kartet flyttes øverst, resten inn i en kolonne ved siden av på brede
 * skjermer. På smale finnes det ingen kolonne å flytte dem til, så de legges
 * sammen i stedet — og de starter sammenlagt, fordi en telefon som åpner med
 * elleve utslåtte paneler er den samme siden som før.
 *
 * ── Hvorfor et sammenlagt panel likevel sier noe ───────────────────────────
 *
 * Hvert panel gir en overskriftsverdi som står i hodet også når det er lukket:
 * budsjettet viser summen, værpanelet viser scoren, klesrådet viser antallet.
 * Å legge sammen skal skjule *utledningen*, aldri svaret — ellers har man byttet
 * en lang side mot en side der man må åpne alt for å vite om det er noe der.
 *
 * ── Valget huskes ──────────────────────────────────────────────────────────
 *
 * Per panel, i localStorage. Den som alltid åpner budsjettet skal ikke måtte
 * åpne det hver gang, og den som aldri bryr seg om lyd skal ikke se det igjen.
 * Et lagret valg slår derfor både standarden og skjermbredden.
 */

/**
 * Bredden der sidekolonnen får plass.
 *
 * Ett tall, brukt av både CSS-en og denne modulen — se `fold.test.ts`, som
 * feiler hvis de to kommer i utakt. To steder å endre er ett sted å glemme, og
 * symptomet ville vært paneler som er lagt sammen på en skjerm som har plass
 * til dem ved siden av.
 */
export const SIDE_COLUMN_MIN_PX = 1100;

const STORAGE_PREFIX = "reise-planer-fold:";

/** Om skjermen er for smal for sidekolonnen. */
export function isNarrow(): boolean {
  try {
    return window.matchMedia(`(max-width: ${SIDE_COLUMN_MIN_PX - 1}px)`).matches;
  } catch {
    // jsdom og eldre nettlesere mangler matchMedia. Anta bred skjerm.
    return false;
  }
}

/** Brukerens eget valg for dette panelet, eller undefined om det ikke finnes. */
export function storedFold(id: string): boolean | undefined {
  try {
    const stored = localStorage.getItem(`${STORAGE_PREFIX}${id}`);
    if (stored === "open") return true;
    if (stored === "shut") return false;
  } catch {
    // Blokkert lagring. Standarden gjelder.
  }
  return undefined;
}

export function rememberFold(id: string, open: boolean): void {
  try {
    localStorage.setItem(`${STORAGE_PREFIX}${id}`, open ? "open" : "shut");
  } catch {
    // Ikke kritisk; valget gjelder fortsatt for denne økten.
  }
}

/**
 * Om panelet skal starte åpent.
 *
 * Rekkefølgen er det som betyr noe: brukerens valg først, så skjermbredden.
 * `openOnWide` er panelets eget ønske på en bred skjerm, og standarden er
 * *lukket* også der.
 *
 * Det siste er meldt fra bruk: «sørg for at alle disse er lukket når vi laster
 * siden». Tre panel som spretter opp samtidig er en side man må rulle forbi
 * før man ser hva appen er, og et sammenlagt panel sier fortsatt hva det
 * inneholder — `summary` står i hodet. Det er nettopp derfor sammenlegging
 * skal skjule utledningen og aldri svaret.
 *
 * `openOnNarrow` er unntaket fra «mobil starter sammenlagt», og det finnes for
 * én grunn: veimeldingene. At Vikafjellet er stengt i morgen tidlig er ikke et
 * oppslag man går til når man er nysgjerrig — det er noe som endrer turen, og
 * et panel som skjuler det bak et trykk er et panel som lar noen kjøre av
 * gårde uten å vite det. Panelet legges derfor sammen når det bare står
 * veiarbeid i det, og står åpent når noe faktisk sperrer. Se
 * `blockingCount` i components/TrafficAlertsPanel.tsx.
 *
 * Brukerens eget valg slår fortsatt begge. Den som har lukket meldingene har
 * sagt hva de vil, og en app som åpner dem igjen overser svaret.
 */
export function foldDefault(id: string, openOnWide: boolean, openOnNarrow = false): boolean {
  const stored = storedFold(id);
  if (stored !== undefined) return stored;
  return isNarrow() ? openOnNarrow : openOnWide;
}
